<template>
  <div class="ms-res">
    <div class="ms-res__head">
      <div class="ms-res__title">
        <b>{{ titleText }}</b>
        <span v-if="searched" class="acq-tiny">{{ subtitle }}</span>
      </div>
      <segmented-control v-if="searched && rows.length" :items="sortItems" :value="sortBy" block label="Sortierung"
                         @input="$emit('update:sortBy', $event)"/>
      <!-- V1: air-line first, routes via Google only on request -->
      <div v-if="searched && rows.length && canCalculate" :class="{ 'ms-calc--done': exactDone }" class="ms-calc">
        <span class="ms-calc__avatar"><v-icon small>{{ exactDone ? 'mdi-check' : 'mdi-map-marker-path' }}</v-icon></span>
        <div class="ms-calc__text">
          <template v-if="exactDone">
            <b>Fahrzeiten {{ travelModeNoun }} berechnet</b>
            <span>Änderst du nur die Sortierung, wird nicht neu abgefragt.<template v-if="rows.length > limit"> Berechnet für die nächsten {{ limit }}.</template></span>
          </template>
          <template v-else>
            <b>Echte Fahrzeiten {{ travelModeNoun }} berechnen?</b>
            <span>Fragt Google für {{ Math.min(rows.length, limit) }} Treffer ab<template v-if="rows.length > limit"> (die nächsten {{ limit }})</template>.</span>
          </template>
        </div>
        <button v-if="!exactDone" :aria-busy="String(calculating)" :disabled="calculating" class="cta cta--primary" type="button" @click="$emit('calculate')">
          <v-progress-circular v-if="calculating" indeterminate size="14" width="2"/>
          Berechnen
        </button>
      </div>
    </div>

    <div ref="list" class="ms-res__list">
      <div v-if="loading" class="pa-2">
        <v-skeleton-loader type="list-item-avatar-two-line@4"/>
      </div>
      <div v-else-if="!searched" class="ms-res__empty">
        <v-icon>mdi-map-marker-radius-outline</v-icon>
        Ausgangspunkt wählen und suchen. Die Treffer erscheinen hier, nach Entfernung sortiert.
      </div>
      <div v-else-if="!rows.length" class="ms-res__empty">
        <v-icon>mdi-map-marker-off-outline</v-icon>
        Keine {{ kind === 'institution' ? 'Standorte' : 'Lehrkräfte' }} {{ areaPhrase }}.
        {{ areaMode ? 'Vergrößere den Bereich' : 'Vergrößere den Umkreis' }} oder lockere die Filter.
      </div>
      <template v-else>
        <div v-for="(row, index) in rows" :id="`ms-row-${row._id}`" :key="row._id" :class="{ 'ms-row--hl': row._id === highlightId }" class="ms-row"
             @mouseenter="$emit('hover', row._id)" @mouseleave="$emit('hover', null)">
          <span :style="{ background: colorOf(row) }" class="ms-row__num">{{ index + 1 }}</span>
          <div class="ms-row__main">
            <span class="ms-row__name">{{ row.name }}</span>
            <span class="ms-row__addr">{{ addressLine(row.address) || 'Keine Adresse' }}</span>
            <div class="ms-row__tags">
              <template v-if="row.kind === 'institution'">
                <span v-if="row.coverage" :class="coverage[row.coverage].tone" class="tag tag--sm">{{ coverage[row.coverage].text }}</span>
                <span v-if="row.acquisitionStatus !== undefined && row.acquisitionStatus !== null" :class="statusTone(row.acquisitionStatus)" class="tag tag--sm">
                  {{ statusLabel(row.acquisitionStatus) }}
                </span>
              </template>
              <template v-else>
                <span v-if="row.hasCar !== null && row.hasCar !== undefined" class="tag tag--sm">
                  <v-icon class="tag__icon" x-small>{{ row.hasCar ? 'mdi-car' : 'mdi-bus' }}</v-icon>{{ row.hasCar ? 'Auto' : 'Ohne Auto' }}
                </span>
                <span v-for="subject in (row.subjects || [])" :key="subject" class="tag tag--sm">{{ subject }}</span>
                <span v-if="row.assignedCount" class="tag tag--sm">{{ row.assignedCount }} {{ row.assignedCount === 1 ? 'Standort' : 'Standorte' }}</span>
                <span v-if="overWish(row)" class="tag tag--sm tone-bad">über Wunsch ({{ formatKm(row.maxDistance) }})</span>
              </template>
            </div>
          </div>
          <div class="ms-row__side">
            <template v-if="routeOf(row)">
              <b>{{ formatMinutes(routeOf(row).duration) }}</b>
              <span>
                <v-icon v-if="routeOf(row).travelMode && travelMode === 'own'" x-small>{{ modeIcon(routeOf(row).travelMode) }}</v-icon>
                {{ formatKm(routeOf(row).distance) }} Strecke
              </span>
            </template>
            <template v-else>
              <b>{{ formatKm(row.airDistance) }}</b>
              <span>{{ noRoute(row) ? 'keine Route' : 'Luftlinie' }}</span>
            </template>
            <div class="ms-row__acts">
              <button :aria-label="viewLabel" v-tip="viewLabel" class="ibtn" type="button" @click="$emit('open', row)">
                <v-icon>mdi-eye-outline</v-icon>
              </button>
              <a :aria-label="'In neuem Tab öffnen'" :href="profileHref(row)" class="ibtn" rel="noopener" target="_blank" v-tip="'In neuem Tab öffnen'">
                <v-icon>mdi-open-in-new</v-icon>
              </a>
              <a v-if="routeUrl(row)" :href="routeUrl(row)" aria-label="Route in Google Maps" class="ibtn" rel="noopener" target="_blank"
                 v-tip="'Route in Google Maps'">
                <v-icon>mdi-directions</v-icon>
              </a>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- V5: the search area as a new acquisition list -->
    <div v-if="canCreateList && searched && rows.length" class="ms-res__foot">
      <button class="cta" type="button" @click="$emit('create-list')">
        <v-icon class="cta__icon" small>mdi-playlist-plus</v-icon>
        Als Akquiseliste anlegen
      </button>
      <span class="acq-tiny">Übernimmt {{ areaMode ? 'den Bereich' : 'den Umkreis als Bereich' }}, die Lehrkraft wählst du danach.</span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Place} from '@/interfaces/place.interface';
import {GoogleTravelModesEnum} from '@/classes/api/google/GoogleTravelModes.enum';
import SegmentedControl, {SegmentItem} from '@/components/common/modern/segmentedControl.vue';
import {formatKm, formatMinutes} from '@/components/acquisitionList/create/acquisitionSearch.helper';
import {statusLabel, statusTone} from '@/components/acquisitionList/ui/acquisitionStatusStyle';
import {addressLine, COVERAGE, googleRouteUrl, MapRow, MapSearchKind, MapSortKey, MapTravelMode, markerColor, RouteInfo} from './mapSearch.helper';

/** Results of the map search: sorting, "Fahrzeiten berechnen" and the numbered list (numbers as on the map) */
export default Vue.extend({
  components: {SegmentedControl},
  props: {
    rows: {type: Array as () => MapRow[], required: true},
    kind: {type: String as () => MapSearchKind, required: true},
    routes: {type: Object as () => Record<string, RouteInfo | null>, required: true},
    sortBy: {type: String as () => MapSortKey, required: true},
    travelMode: {type: String as () => MapTravelMode, required: true},
    travelModeNoun: {type: String, default: ''},
    origin: {type: Object as () => Place | null, default: null},
    radiusKm: {type: Number, required: true},
    highlightId: {type: String, default: null},
    searched: {type: Boolean, default: false},
    loading: {type: Boolean, default: false},
    calculating: {type: Boolean, default: false},
    canCalculate: {type: Boolean, default: false},
    exactDone: {type: Boolean, default: false},
    limit: {type: Number, required: true},
    areaMode: {type: Boolean, default: false},
    canCreateList: {type: Boolean, default: false},
  },
  data: () => ({
    coverage: COVERAGE,
  }),
  computed: {
    noun(): string {
      if (this.kind === 'institution') {
        return this.rows.length === 1 ? 'Standort' : 'Standorte';
      }
      return this.rows.length === 1 ? 'Lehrkraft' : 'Lehrkräfte';
    },
    titleText(): string {
      if (!this.searched) {
        return 'Treffer';
      }
      return `${this.rows.length} ${this.noun}`;
    },
    radiusText(): string {
      return this.radiusKm.toLocaleString('de-DE');
    },
    areaPhrase(): string {
      return this.areaMode ? 'im gezeichneten Bereich' : `im Umkreis von ${this.radiusText} km`;
    },
    subtitle(): string {
      const order = this.sortBy === 'time' ? 'Fahrzeit' : this.sortBy === 'name' ? 'Name' : 'Luftlinie';
      return `${this.areaPhrase}, sortiert nach ${order}`;
    },
    sortItems(): SegmentItem[] {
      return [
        {value: 'air', text: 'Luftlinie'},
        {value: 'time', text: 'Fahrzeit', disabled: !this.exactDone, reason: 'Erst Fahrzeiten berechnen'},
        {value: 'name', text: 'Name'},
      ];
    },
    viewLabel(): string {
      return this.kind === 'institution' ? 'Standortprofil ansehen' : 'Lehrkraft ansehen';
    },
  },
  methods: {
    addressLine,
    formatKm,
    formatMinutes,
    statusLabel,
    statusTone,
    colorOf: (row: MapRow) => markerColor(row),
    routeOf(row: MapRow): RouteInfo | null {
      return this.routes[row._id] || null;
    },
    /** Google was asked for this row but found no route */
    noRoute(row: MapRow): boolean {
      return row._id in this.routes && !this.routes[row._id];
    },
    modeIcon(mode: GoogleTravelModesEnum): string {
      return GoogleTravelModesEnum.Record[mode]?.icon || 'mdi-map-marker-path';
    },
    overWish(row: MapRow): boolean {
      if (!row.maxDistance) {
        return false;
      }
      const distance = this.routeOf(row)?.distance ?? row.airDistance;
      return distance !== null && distance > row.maxDistance;
    },
    profileHref(row: MapRow): string {
      return this.$router.resolve(row.kind === 'institution' ? `/institutions/${row._id}` : `/teachers/${row._id}`).href;
    },
    routeUrl(row: MapRow): string | null {
      const mode = this.routeOf(row)?.travelMode || (this.travelMode === 'own' ? undefined : this.travelMode);
      return googleRouteUrl(this.origin, row.address, mode);
    },
    /** Scrolls the row into view (marker click on the map) */
    reveal(id: string) {
      const el = (this.$refs.list as HTMLElement | undefined)?.querySelector(`#ms-row-${id}`);
      el?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    },
  },
});
</script>

<style scoped>
.ms-res {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.ms-res__head {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 16px 10px;
}

.ms-res__title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.ms-res__title b {
  font-size: 15px;
  font-weight: 600;
}

.ms-calc {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, .04);
}

.ms-calc__avatar {
  display: grid;
  place-items: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #E8F1FD;
}

.ms-calc__avatar .v-icon {
  color: #0D47A1 !important;
}

.ms-calc--done .ms-calc__avatar {
  background: #E7F6EC;
}

.ms-calc--done .ms-calc__avatar .v-icon {
  color: #1B5E20 !important;
}

.ms-calc__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ms-calc__text b {
  font-size: 13px;
  font-weight: 600;
}

.ms-calc__text span {
  font-size: 12px;
  opacity: .7;
}

.ms-res__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 12px;
}

.ms-res__foot {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid rgba(0, 0, 0, .08);
}

.ms-res__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 8px;
  padding: 24px 16px;
  border: 1.5px dashed rgba(15, 23, 42, .14);
  border-radius: 12px;
  font-size: 13px;
  text-align: center;
  opacity: .8;
}

.ms-row {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  padding: 10px 8px;
  border-radius: 12px;
}

.ms-row:hover, .ms-row--hl {
  background: rgba(15, 23, 42, .04);
}

.ms-row__num {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #FFFFFF;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ms-row__main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ms-row__name {
  overflow: hidden;
  font-size: 13.5px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ms-row__addr {
  overflow: hidden;
  font-size: 12px;
  opacity: .6;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ms-row__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
}

.ms-row__side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
}

.ms-row__side b {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.ms-row__side > span {
  font-size: 11.5px;
  opacity: .6;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.ms-row__side > span .v-icon {
  color: inherit !important;
}

.ms-row__acts {
  display: flex;
  gap: 2px;
  margin-top: 4px;
}
</style>
